import React, { useContext, useEffect, useState } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-react';
import { useForm } from 'react-hook-form';

import { getAPICall } from '../../dataFetcher/axiosMethodCalls';
import { ProcessOrderValidationService } from '../CheckoutPage/ProcessOrderValidationService';
import { SubmitProcessValidationService } from '../SubmitPaymentProcess/SubmitProcessValidationService';
import { APIWebstoreContext } from '../../context/globalContext';
import nextId from 'react-id-generator';
import { INVOICE_LIST } from '../../context/reducer';
import BillingSaveModal from '../ShippingInformation/BillingSavePopup';
import { FaRegEdit } from 'react-icons/fa';
import { postAPICall } from '../../dataFetcher/axiosMethodCalls';
import ToastNotification from '../ToastNotification/ToastNotification';
import { DialogActions, DialogContent, DialogTitle } from '../DialogComponent/DialogComponent';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import { ShippingAddressDetails } from '../ShippingInformation/ShippingInformation';
import { ProfileBillingAddress, TempBillingAddress } from '../../utils/constants';

export interface IBillingAddress {
  AddressId?: string;
  FirstName?: string;
  LastName?: string;
  Street1: string;
  Street2?: string;
  City: string;
  State: string;
  Zip: string;
  Country: string;
  Phone: string;
}

const BillingAddressInitialState: IBillingAddress = {
  FirstName: '',
  LastName: '',
  Street1: '',
  Street2: '',
  City: '',
  State: '',
  Zip: '',
  Country: '',
  Phone: '',
};

export interface ICountry {
  country: string;
  countryCode: string;
}

type BillingAddressProps = {
  fields: {
    BillingAddressInfo: string;
  };
  invoiceIdentifier?: string;
};

const BillingAddress = ({ fields, invoiceIdentifier }: BillingAddressProps): React.JSX.Element => {
  const showFullBillingAddress = invoiceIdentifier
    ? INVOICE_LIST.includes(invoiceIdentifier)
    : false;
  const [countries, setCountries] = useState<Array<ICountry>>([]);
  const [billingAddress, setBillingAddress] = useState<IBillingAddress>(BillingAddressInitialState);
  const [isBillingSame, setIsBillingSame] = useState(!showFullBillingAddress);
  const [countryName, setCountryName] = useState('');
  const { state, dispatch } = useContext(APIWebstoreContext);
  const ValidationService = invoiceIdentifier
    ? SubmitProcessValidationService
    : ProcessOrderValidationService;

  const {
    register,
    trigger,
    handleSubmit,
    getValues,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onChange',
  });

  const getBillingAddress = (data: any) => {
    setBillingAddress({
      FirstName: data.firstName,
      LastName: data.lastName,
      Street1: data.street,
      Street2: data.unit,
      City: data.city,
      State: data.state,
      Zip: data.postal,
      Country: data.country,
      Phone: data.phone,
    });
  };

  useEffect(() => {
    getCountries();
    ProcessOrderValidationService.setBillingSameAsShipping(isBillingSame);
    ValidationService.setBillingInfo({}, false);
    if (invoiceIdentifier) {
      ValidationService.setBillingInfo(getValues, false);
    }
  }, []);

  useEffect(() => {
    if (!isBillingSame) {
      validateAddress();
    }
  }, [state?.toggleProcessOrderValidation]);

  useEffect(() => {
    ValidationService.setBillingInfo(getValues, false);
    validateAddress();
  }, [state?.toggleSubmitPaymentValidation]);

  const validateAddress = () => {
    trigger();

    let billAddress = getValues();
    let hasErrors = false;

    if (
      billAddress.country?.length < 2 ||
      billAddress.city === '' ||
      billAddress.firstName === '' ||
      billAddress.lastName === '' ||
      billAddress.postal === '' ||
      billAddress.state === '' ||
      billAddress.street === ''
    ) {
      ValidationService.setErrorMessage('Please check all the required Billing address details!');

      hasErrors = true;
    }
  };

  function getCountries() {
    getAPICall(`${process.env.REACT_APP_PROFILE_URL}/countries`).then(function (response: any) {
      let countryList = [{ country: 'Country', countryCode: '' }];

      if (response && response.data) {
        var responseList = response.data.map((item: any) => ({
          country: item.api_name,
          countryCode: item.api_isocountrycode,
        }));
        countryList = [...countryList, ...responseList];
      }

      setCountries(countryList);
    });
  }

  const toggleBillingAddressDisplay = (e: any) => {
    const target = e.target.checked;
    setIsBillingSame(target);
    ProcessOrderValidationService.setBillingSameAsShipping(target);

    if (target) {
      ValidationService.setBillingInfo({}, false);
    } else {
      ValidationService.setBillingInfo(getValues, false);
    }
  };

  function handleCountryName(e: any) {
    setCountryName(e.target.value);
  }

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddressValidationOpen, setIsAddressValidationOpen] = useState(false);
  const [savedValues, setSavedValues] = useState<any>(null);
  const openModal = () => {
    setIsModalOpen(true);
  };

  type BillingAddressOption = typeof ProfileBillingAddress | typeof TempBillingAddress;

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const [isBillingSaved, setIsBillingSaved] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [customToastMessage, setCustomToastMessage] = useState({
    message: '',
  });
  const [addressValidationOption, setAddressValidationOption] =
    useState<BillingAddressOption | null>(null);
  const [addressValidationData, setAddressValidationData] = useState<any>(null);

  const openAddressValidationPopup = (data: any, option: BillingAddressOption) => {
    setAddressValidationData(data);
    setAddressValidationOption(option);
    setIsAddressValidationOpen(true);
  };

  const handleConfirmPopupClose = () => {
    setIsAddressValidationOpen(false);
  };

  const handlePopupYes = async () => {
    if (addressValidationOption === ProfileBillingAddress) {
      // save the address in my profile
      await sendBillingAddressApiCall(addressValidationData);
      setIsAddressValidationOpen(false);
      return;
    }
    setToastType('success');
    setCustomToastMessage({
      message: state?.sitecoreDictionaryData?.BillingAddressSaved ?? 'Billing address saved',
    });
    setShowToast(true);
    setIsBillingSaved(true);
    ProcessOrderValidationService.setIsBillingAddressSaved(true);
    setSavedValues(addressValidationData);
    setIsEditMode(false);
    setIsAddressValidationOpen(false);
    ProcessOrderValidationService.setBillingInfo(getValues, false);
    updateInvoiceCountry(addressValidationData?.country);
  };

  const toastRemove = () => {
    setShowToast(false);
  };

  const handleBillingSave = async (
    option: typeof ProfileBillingAddress | typeof TempBillingAddress
  ) => {
    ProcessOrderValidationService.setBillingAddressOption(option);
    var formData = getValues();
    if (option === ProfileBillingAddress) {
      var shippingAddresses = await getShippingAddressesApiCall();
      const otherAddressesCount = shippingAddresses.filter((x) => !x.IsPrimary).length;
      if (otherAddressesCount >= 5) {
        setToastType('error');
        setCustomToastMessage({
          // read it from dictonary
          message:
            state?.sitecoreDictionaryData?.AddressLimitError ??
            'Cannot save address. Maximum number of addresses exceeded. Navigate to your Account Profile to edit your addresses. ',
        });
        setShowToast(true);
        setIsBillingSaved(false);
        ProcessOrderValidationService.setIsBillingAddressSaved(false);
        setIsEditMode(false);
        ValidationService.setBillingInfo(getValues, false);
        return;
      } else {
        await handleBillingAddressSave(formData, option);
      }
    } else {
      await handleBillingAddressSave(formData, option);
    }
  };

  async function getShippingAddressesApiCall(): Promise<ShippingAddressDetails[]> {
    try {
      const response = await getAPICall(
        `${process.env.REACT_APP_PROFILE_URL}/profile/getaddresses`
      );

      if (response?.data?.length) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching shipping addresses:', error);
      return [];
    }
  }

  const [isEditMode, setIsEditMode] = useState(false);

  const updateInvoiceCountry = (country: string) => {
    if (dispatch) {
      dispatch({
        type: 'INVOICE_UPDATE',
        payload: {
          groupAdminCountry: country,
        },
      });
    }
  };

  const handleBillingAddressSave = async (data: any, option: BillingAddressOption) => {
    const validationResult = await validateBillingAddress(data);

    const { statusResponse, addressValidationStatusCode } = validationResult;

    if (statusResponse === true && addressValidationStatusCode !== 'I') {
      // save the address
      if (option === ProfileBillingAddress) {
        // save it as permanent address
        await sendBillingAddressApiCall(data);
        return;
      }
      setToastType('success');
      setCustomToastMessage({
        message: state?.sitecoreDictionaryData?.BillingAddressSaved ?? 'Billing address saved.',
      });
      setShowToast(true);
      setIsBillingSaved(true);
      ProcessOrderValidationService.setIsBillingAddressSaved(true);
      setSavedValues(data);
      setIsEditMode(false);
      ProcessOrderValidationService.setBillingInfo(getValues, false);
      updateInvoiceCountry(data?.country);
    } else {
      if (addressValidationStatusCode && String(addressValidationStatusCode) === 'I') {
        // open confirmation
        openAddressValidationPopup(data, option);
      } else {
        // show the error message
        setToastType('error');
        setCustomToastMessage({
          message:
            state?.sitecoreDictionaryData?.AddressValidationErrorMessage ??
            'Address Validation Error, please check if the address is correct.',
        });
        setShowToast(true);
        setIsBillingSaved(false);
        ProcessOrderValidationService.setIsBillingAddressSaved(false);
        setIsEditMode(false);
      }
    }
  };

  // address validation
  const validateBillingAddress = async (data: any) => {
    let APIResponse: any = {};
    let statusResponse: any;
    let addressValidationStatusCode: any;

    const validateShippingAddressRequest = {
      city: data.city,
      country: data.country,
      line1: data.street,
      line2: data.unit,
      postalCode: data.postal,
      region: data.state,
    };
    try {
      APIResponse = await postAPICall(
        `${process.env.REACT_APP_PAYMENT_URL}/address/ValidateAddress`,
        validateShippingAddressRequest
      );
    } catch {
      return { statusResponse: false, addressValidationStatusCode };
    }
    if (data.country === 'US' || data.country === 'CA') {
      statusResponse = APIResponse.data.status;
      addressValidationStatusCode = undefined;
    } else {
      statusResponse = APIResponse.data.status;
      addressValidationStatusCode = APIResponse.data.statusCode;
    }

    return { statusResponse, addressValidationStatusCode };
  };

  const onValidSubmit = (data: any) => {
    openModal();
  };

  const onInvalidSubmit = () => {};

  const handleCancelEdit = () => {
    if (savedValues) {
      reset(savedValues);
    }
    setIsEditMode(false);
    setIsBillingSaved(true);
    ProcessOrderValidationService.setIsBillingAddressSaved(true);
    ProcessOrderValidationService.setBillingInfo(getValues, false);
  };

  // save address function
  function sendBillingAddressApiCall(data: any) {
    const sendToShippingAddressesRequest = {
      FirstName: data.firstName,
      LastName: data.lastName,
      Street1: data.street,
      Street2: data.unit,
      City: data.city,
      State: data.state,
      Zip: data.postal,
      Country: data.country,
      IsShipping: true,
      IsBilling: true,
    };

    postAPICall(
      `${process.env.REACT_APP_PROFILE_URL}/profile/upsertaddress`,
      sendToShippingAddressesRequest
    )
      .then(function (response: any) {
        if (response && response.status === 200) {
          ProcessOrderValidationService.setBillingAddressId(response?.data?.AddressId);
          // success toast
          setToastType('success');
          setCustomToastMessage({
            message: state?.sitecoreDictionaryData?.BillingAddressSaved ?? 'Billing address saved.',
          });
          setShowToast(true);
          setIsBillingSaved(true);
          ProcessOrderValidationService.setIsBillingAddressSaved(true);
          setSavedValues(data);
          setIsEditMode(false);
          ProcessOrderValidationService.setBillingInfo(getValues, false);
        }
      })
      .catch((error) => {
        // failure toast
        setToastType('error');
        setCustomToastMessage({
          message:
            state?.sitecoreDictionaryData?.ReenterBillingAddress ??
            'Billing address not saved. Please reenter.',
        });
        setShowToast(true);
        setIsBillingSaved(false);
        setIsEditMode(false);
      });
  }

  return (
    <>
      {!showFullBillingAddress && (
        <div className="billing-checkbox-container CreditCardAddress">
          <input
            id="addresconfirm"
            type="checkbox"
            className="checkbox"
            defaultChecked={isBillingSame}
            onChange={toggleBillingAddressDisplay}
          />
          <span className="addressinfoheader"> Billing address is same as shipping</span>
        </div>
      )}

      {!showFullBillingAddress && <span className="billlinfo">{fields?.BillingAddressInfo}</span>}
      {(!isBillingSame || showFullBillingAddress) && (
        <>
          <div className="billingaddressheader">
            <div className="billingaddressinfoheader"> Billing Address:</div>
            {
              <>
                {isEditMode && (
                  <div className="savecancelaction">
                    <a
                      href="#"
                      className="savelink"
                      onClick={(e) => {
                        e.preventDefault();
                        handleCancelEdit();
                      }}
                    >
                      Cancel
                    </a>
                    <a
                      href="#"
                      className="savelink"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubmit(onValidSubmit, onInvalidSubmit)();
                      }}
                    >
                      Save
                    </a>
                  </div>
                )}
                {!isBillingSaved && !isEditMode && (
                  <a
                    href="#"
                    className="savelink"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmit(onValidSubmit, onInvalidSubmit)();
                    }}
                  >
                    Save
                  </a>
                )}
                {isBillingSaved && !isEditMode && (
                  <div className="editordeleteaction">
                    <div className="editaction">
                      <FaRegEdit></FaRegEdit>
                      <a
                        href="#"
                        className="editlink"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsEditMode(true);
                          setIsBillingSaved(false);
                          ProcessOrderValidationService.setIsBillingAddressSaved(false);
                        }}
                      >
                        Edit
                      </a>
                    </div>
                  </div>
                )}
              </>
            }
          </div>
          <form onSubmit={handleSubmit(getBillingAddress)}>
            <fieldset className="billingformset" disabled={isBillingSaved && !isEditMode}>
              <div className="billing-form">
                <div className="billing-form-row">
                  <span className="billing-form-row-header">
                    Country
                    {errors.country && <span className="text-error">Required</span>}
                  </span>
                  <select
                    className="inputtext"
                    value={countryName}
                    {...register('country', {
                      required: true,
                      minLength: 2,
                      onChange: handleCountryName,
                    })}
                  >
                    {countries.map((item) => (
                      <option key={nextId()} value={item.countryCode}>
                        {item.country}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="billing-form-row-group">
                  <div className="billing-form-row">
                    <span className="billing-form-row-header">
                      First Name
                      {errors.firstName && <span className="text-error">Required</span>}
                    </span>
                    <input
                      type="text"
                      placeholder="First Name"
                      {...register('firstName', { required: true })}
                    ></input>
                  </div>
                  <div className="billing-form-row">
                    <span className="billing-form-row-header">
                      Last Name
                      {errors.lastName && <span className="text-error">Required</span>}
                    </span>
                    <input
                      type="text"
                      placeholder="Last Name"
                      {...register('lastName', { required: true })}
                    ></input>
                  </div>
                </div>
                <div className="billing-form-row">
                  <span className="billing-form-row-header">
                    Street Address or P.O. Box{' '}
                    {errors.street && <span className="text-error">Required</span>}
                  </span>
                  <input
                    type="text"
                    maxLength={50}
                    className="inputtext"
                    {...register('street', { required: true })}
                    placeholder="Street Address or P.O. Box"
                  ></input>
                </div>
                <div className="billing-form-row-group">
                  <div className="billing-form-row">
                    <span className="billing-form-row-header">
                      Apt, Suite, Unit, Building, Floor, etc.
                    </span>
                    <input
                      type="text"
                      maxLength={50}
                      className="inputtext"
                      {...register('unit', { required: false })}
                      placeholder="Unit"
                    ></input>
                  </div>
                  <div className="billing-form-row">
                    <span className="billing-form-row-header">
                      City
                      {errors.city && <span className="text-error">Required</span>}
                    </span>
                    <input
                      type="text"
                      maxLength={50}
                      className="inputtext"
                      {...register('city', { required: true })}
                      placeholder="City"
                    ></input>
                  </div>
                </div>
                <div className="billing-form-row-group">
                  <div className="billing-form-row">
                    <span className="billing-form-row-header">
                      State/Province {errors.state && <span className="text-error">Required</span>}
                    </span>
                    <input
                      type="text"
                      maxLength={50}
                      className="inputtext"
                      {...register('state', { required: true })}
                      placeholder="State"
                    ></input>
                  </div>
                  <div className="billing-form-row">
                    <span className="billing-form-row-header">
                      Postal Code {errors.postal && <span className="text-error">Required</span>}
                    </span>
                    <input
                      type="text"
                      maxLength={20}
                      className="inputtext"
                      {...register('postal', { required: true })}
                      placeholder="Postal Code"
                    ></input>
                  </div>
                </div>
              </div>
            </fieldset>
          </form>
          <BillingSaveModal isOpen={isModalOpen} onClose={closeModal} onSave={handleBillingSave} />
          <ToastNotification
            fields={{
              ToastMessage: {
                value: customToastMessage.message,
              } as Field<string>,
            }}
            showToast={showToast}
            removeToast={toastRemove}
            toastStatus={toastType}
          ></ToastNotification>
          <Modal
            open={isAddressValidationOpen}
            onClose={(_: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
              if (reason !== 'backdropClick') {
                handleConfirmPopupClose();
              }
            }}
            className="ws-modal-dialog"
          >
            <div className="addressValidationDialog addressValidationDialogPaper">
              <DialogTitle id="customized-dialog-title" onClose={handleConfirmPopupClose}>
                Address Validation
              </DialogTitle>

              <DialogContent dividers>
                {
                  'Unable to validate your address. Do you want to save without validating the address?'
                }
              </DialogContent>

              <DialogActions>
                <Button onClick={handlePopupYes} className="ws-primary-button-no-border">
                  YES
                </Button>

                <Button onClick={handleConfirmPopupClose} className="ws-white-button">
                  NO
                </Button>
              </DialogActions>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default BillingAddress;
